import { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';

// Circle Hub concierge calendar - only this link triggers the concierge flow with product context
const CIRCLE_HUB_CALENDAR = 'https://link.circlehub.org/widget/booking/XMerHP4ILIgxbU9odtIS';

interface UseBookingModalReturn {
  openBooking: (planName?: string) => void;
  BookingModal: React.FC;
  PreloadIframe: React.FC;
}

interface ProductContext {
  id?: string;
  name: string;
  vendorEmail?: string;
  vendorPhone?: string;
}

/**
 * Build a booking URL with product context appended as query params
 * Only applies to Circle Hub calendar (concierge flow)
 * Other booking URLs go directly to the vendor without modification
 */
function buildBookingUrlWithContext(baseUrl: string, product: ProductContext): string {
  // Only append product context if using Circle Hub calendar (concierge flow)
  const isCircleHubCalendar = baseUrl.startsWith(CIRCLE_HUB_CALENDAR);
  
  if (!isCircleHubCalendar) {
    // Direct vendor booking - no modifications
    return baseUrl;
  }
  
  try {
    const url = new URL(baseUrl);
    
    // Append product context for the webhook to capture
    if (product.id) {
      url.searchParams.set('product_id', product.id);
    }
    url.searchParams.set('product_name', product.name);
    if (product.vendorEmail) {
      url.searchParams.set('vendor_email', product.vendorEmail);
    }
    if (product.vendorPhone) {
      url.searchParams.set('vendor_phone', product.vendorPhone);
    }
    
    return url.toString();
  } catch {
    // If URL parsing fails, append params manually
    const separator = baseUrl.includes('?') ? '&' : '?';
    const params = new URLSearchParams();
    if (product.id) params.set('product_id', product.id);
    params.set('product_name', product.name);
    if (product.vendorEmail) params.set('vendor_email', product.vendorEmail);
    if (product.vendorPhone) params.set('vendor_phone', product.vendorPhone);
    return `${baseUrl}${separator}${params.toString()}`;
  }
}

export const useBookingModal = (
  productName: string, 
  bookingUrl?: string,
  productContext?: ProductContext
): UseBookingModalReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasPreloaded, setHasPreloaded] = useState(false);

  // Build the final URL with context params
  const finalBookingUrl = useMemo(() => {
    if (!bookingUrl) return undefined;
    
    const context: ProductContext = productContext || { name: productName };
    return buildBookingUrlWithContext(bookingUrl, context);
  }, [bookingUrl, productContext, productName]);

  // Reset loaded state when URL changes
  useEffect(() => {
    setIsLoaded(false);
    setHasPreloaded(false);
  }, [finalBookingUrl]);

  const openBooking = (planName: string = 'Discovery Call') => {
    setSelectedPlan(planName);
    setIsOpen(true);
  };

  const handleIframeLoad = () => {
    setIsLoaded(true);
  };

  const handlePreloadComplete = () => {
    setHasPreloaded(true);
    setIsLoaded(true);
  };

  // Hidden preload iframe - renders invisibly to warm the cache
  const PreloadIframe = () => {
    if (!finalBookingUrl || hasPreloaded) return null;
    
    return (
      <iframe
        src={finalBookingUrl}
        onLoad={handlePreloadComplete}
        style={{
          position: 'absolute',
          width: 0,
          height: 0,
          border: 'none',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
        tabIndex={-1}
        aria-hidden="true"
        title="Preload calendar"
      />
    );
  };

  const BookingModal = () => (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Book a Call - {productName}</DialogTitle>
          <DialogDescription>
            {selectedPlan !== 'Discovery Call' && `Selected plan: ${selectedPlan}`}
          </DialogDescription>
        </DialogHeader>
        <div className="w-full h-[600px] relative">
          {/* Loading spinner - shows until iframe loads */}
          {!isLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-muted rounded-lg z-10">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Loading calendar...</p>
              </div>
            </div>
          )}
          
          {finalBookingUrl ? (
            <iframe
              src={finalBookingUrl}
              onLoad={handleIframeLoad}
              className="transition-opacity duration-300"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                opacity: isLoaded ? 1 : 0,
              }}
              title="Schedule a call"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted rounded-lg">
              <p className="text-muted-foreground">No booking URL configured</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

  return { openBooking, BookingModal, PreloadIframe };
};
